import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { Calendar as CalendarIcon, ShieldCheck, HardDrive, ChevronLeft } from "lucide-react";
import { api } from "../../lib/api";
import SmartBackupCalendar from "../SmartBackupCalendar";

/**
 * Compact "גיבויים חכמים" button — merges countdown timer + storage
 * quota inline so the user sees both at a glance in a single tile.
 *
 * Click → opens the existing full SmartBackupCalendar modal.
 *
 * Layout (RTL):
 *  ┌───────────────────────────────────────────────────────────┐
 *  │ 📅 גיבויים חכמים        ✓ 18:26:05    💾 35.1%   ›        │
 *  └───────────────────────────────────────────────────────────┘
 */
export default function SmartBackupCompact({ companyId, onOpen }) {
  const [next, setNext] = useState(null);
  const [quota, setQuota] = useState(null);
  const [now, setNow] = useState(Date.now());
  const [open, setOpen] = useState(false);

  // If parent supplies onOpen, use it (lifts modal to parent so the
  // ClientAdminSidebar can close itself before opening the modal — fixes
  // the visual stacking where the side-drawer stayed mounted behind the
  // calendar). Otherwise fall back to managing the modal locally.
  const handleClick = () => {
    if (onOpen) onOpen();
    else setOpen(true);
  };

  useEffect(() => {
    if (!companyId) return;
    let cancelled = false;
    const fetchAll = async () => {
      try {
        const [n, q] = await Promise.all([
          api.get(`/companies/${companyId}/smart-snapshots/next-backup`).then(r => r.data).catch(() => null),
          api.get(`/companies/${companyId}/smart-snapshots/quota`).then(r => r.data).catch(() => null),
        ]);
        if (cancelled) return;
        setNext(n?.next_backup_at || null);
        setQuota(q || null);
      } catch { /* silent */ }
    };
    fetchAll();
    const sync = setInterval(fetchAll, 60_000);
    return () => { cancelled = true; clearInterval(sync); };
  }, [companyId]);

  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);

  const ms = next ? Math.max(0, new Date(next).getTime() - now) : 0;
  const totalSec = Math.floor(ms / 1000);
  const h = Math.floor(totalSec / 3600);
  const m = Math.floor((totalSec % 3600) / 60);
  const s = totalSec % 60;
  const pct = quota?.percent ?? 0;
  const pctState = pct >= 90 ? "critical" : pct >= 75 ? "warning" : "ok";
  const pctColor = pctState === "critical" ? "text-rose-300" : pctState === "warning" ? "text-amber-300" : "text-emerald-300";

  return (
    <>
      <button
        type="button"
        onClick={handleClick}
        className="group w-full rounded-2xl border border-emerald-600/40 bg-gradient-to-l from-emerald-950/50 via-[#0a1429]/60 to-cyan-950/40 hover:from-emerald-900/60 hover:to-cyan-900/50 transition shadow-md hover:shadow-emerald-500/20 overflow-hidden"
        data-testid="open-smart-backup-calendar"
      >
        {/* Top row: title + chevron */}
        <div className="px-3.5 pt-2.5 pb-1.5 flex items-center justify-between gap-2">
          <span className="inline-flex items-center gap-1.5 text-[13px] font-extrabold text-emerald-100">
            <CalendarIcon className="h-3.5 w-3.5 text-emerald-300" />
            גיבויים חכמים
          </span>
          <ChevronLeft className="h-3.5 w-3.5 text-emerald-300/70 group-hover:-translate-x-0.5 transition" />
        </div>

        {/* Bottom row: timer + storage inline */}
        <div className="px-3.5 pb-2.5 flex items-center justify-between gap-3">
          {/* Storage in credits (compact) */}
          <div className="inline-flex items-center gap-1.5 min-w-0" data-testid="sbc-storage">
            <HardDrive className={`h-3.5 w-3.5 ${pctColor} shrink-0`} />
            <div className="flex flex-col leading-none">
              <span className="text-[10px] uppercase tracking-widest font-bold text-slate-400">אחסון</span>
              <span className={`text-xs font-extrabold tabular-nums ${pctColor}`}>
                {Math.round(pct)}%
                <span className="text-[9px] text-slate-500 mr-1">
                  {quota?.used_mb ? `${quota.used_mb.toFixed(1)}MB` : ""}
                </span>
              </span>
            </div>
          </div>

          {/* Countdown timer (compact) */}
          <div className="inline-flex items-center gap-1.5" data-testid="sbc-timer">
            <ShieldCheck className="h-3.5 w-3.5 text-cyan-300 shrink-0" />
            <div className="flex flex-col leading-none items-end">
              <span className="text-[10px] uppercase tracking-widest font-bold text-slate-400">גיבוי הבא בעוד</span>
              <span className="text-xs font-extrabold text-white tabular-nums">
                {String(h).padStart(2, "0")}:{String(m).padStart(2, "0")}:{String(s).padStart(2, "0")}
              </span>
            </div>
          </div>
        </div>

        {/* Progress bar */}
        <div className="h-0.5 bg-emerald-950/40 relative overflow-hidden">
          <div
            className={`absolute right-0 top-0 bottom-0 ${pctState === "critical" ? "bg-rose-500" : pctState === "warning" ? "bg-amber-500" : "bg-emerald-500"}`}
            style={{ width: `${Math.max(2, pct)}%` }}
          />
        </div>
      </button>

      {open && createPortal(
        <SmartBackupCalendar companyId={companyId} onClose={() => setOpen(false)} />,
        document.body
      )}
    </>
  );
}
